// Filtering and ranking for the relation tree (owner ruling 2026-07-30):
// grouping by relation type is replaced by a viewset-shaped control — a filter
// over which relations show, plus a ranking rule that orders them.
//
// This is the same primitive the project already ruled for page-level lists
// ("every view is a viewset + a ranking rule", TODO complexity sitting; and the
// 2026-07-03 UX ruling that viewsets are the subreddit primitive and the
// resolve queue is a RANKING RULE inside them). The tree is the first place it
// lands below page level.
//
// Kept pure and free of React on purpose: no imports, no fetch, no hooks. The
// project has no frontend unit harness (a named gap in records/INSTRUMENTS.md),
// so the next best thing is logic that can be read in one sitting and driven
// through the render with scripts/ui_drive.js.
//
// ── WHY LOSING THE GROUP HEADERS IS SAFE ────────────────────────────────────
// A group header used to be the only place the relation name appeared. It is
// not: EdgeSentence renders every row as "⟨subject⟩ ⟨verb⟩ ⟨object⟩", so the
// relation is in the sentence itself, coloured the same way the header was.
// The header carried scannability, not information — which is what the filter
// chips now carry, and better, because you can subtract as well as scan.

// ── THE RANKING RULES ────────────────────────────────────────────────────────
//
// ⚠ `hot` is engagement, and it is here by an explicit owner ruling on
// 2026-07-30 that widened DECISIONS.md 2026-07-05 (which had confined hot to
// the lobby). Three properties keep it from becoming an input rather than a
// view, and they are the conditions the ruling was given under:
//
//   1. It never reaches a lens or engine — comment relations are excluded from
//      the graph the trust math reads, so this cannot move a score.
//   2. It is never the DEFAULT. An opt-in sort is a viewer choosing engagement;
//      a default is engagement choosing for every viewer, which is a different
//      thing wearing the same name.
//   3. It reorders a complete list and never shortens one. The engagement
//      tripwire in backend/trust/graph.ts names "order or hide" together, and
//      hiding is the half that makes an unread but well-evidenced claim vanish.
//
// If a future change makes hot the default, or lets it filter, it has broken
// the terms this was built under and needs a new ruling, not a patch.
export const SORTS = [
  {
    key: 'contested',
    label: 'contested',
    hint: 'widest named tension first — the same reading the front door ranks by',
  },
  {
    key: 'needs_judgment',
    label: 'needs judgment',
    hint: 'unvoted edges first, longest-waiting among them — where a vote produces signal',
  },
  { key: 'new', label: 'new', hint: 'most recently drawn first' },
  { key: 'strongest', label: 'strongest', hint: 'highest under your lens weights first' },
  {
    // The sixth rule, asked for on 2026-07-30 ("why not try all of them") and
    // built 2026-07-31. NOT the inverse of needs_judgment: that one puts inert
    // edges first to drain a queue; this puts the heavily-argued first —
    // where the community has already done the work.
    key: 'most_adjudicated',
    label: 'most adjudicated',
    hint: 'most votes on the connection first — where the community has already done the work',
  },
  {
    key: 'hot',
    label: 'hot',
    hint: 'most attention first — edges drawn and votes cast, which never enter a trust score',
  },
];

// `OBJECT_SORTS` was here until 2026-08-07. The object-grain rule set now
// lives in backend/lib/streamRank.ts and ships in the /api/stream payload, so
// the pills advertise what the server actually implements. It excluded
// `most_adjudicated` for a reason that still holds and is restated there in
// its own terms: that rule counts votes ON AN EDGE, and a bare object row
// carries none.

// Contested, not hot, and not new. It is the ranking the project's own thesis
// points at, and it is what the front door leads with.
//
// Note it reflows once, when the lens scores land: contested cannot be computed
// from the edge rows alone. That reflow is ~40ms after the shape-③ cache
// (2026-07-30); before it, ordering by contested would have meant a full second
// of visibly wrong order, which is why this was not worth doing until now.
export const DEFAULT_SORT = 'contested';

// Tag edges are hidden by default and are now FILTERABLE rather than dropped.
// Before this change the tree silently discarded outgoing `tagged` edges, so
// there was no way to see an object's tags here at all. Hiding them by default
// keeps the tree about argument; the chip is what makes them reachable, which
// is what the owner asked for ("arbitrarily see relations / tags").
export const DEFAULT_HIDDEN_RELATIONS = ['tagged'];

export const farEndId = (edge, fromId) =>
  edge.source_id === fromId ? edge.target_id : edge.source_id;

export const isActivated = (edge) => edge.up_votes + edge.down_votes > 0;

// Newest first, then id — a total order, so rows never shuffle between renders
// when the primary key ties. Every comparator below falls through to this.
function byRecency(a, b) {
  const ta = Date.parse(a.created_at || '') || 0;
  const tb = Date.parse(b.created_at || '') || 0;
  if (tb !== ta) return tb - ta;
  return String(a.id).localeCompare(String(b.id));
}

// Rank of a tension state: disputed first, then anything with a reading at all,
// then no signal. Unscored far ends (a question carries no truth value, so no
// lens reading of one is meaningful) sort last rather than as a zero — the same
// absence-not-zero rule the rest of the app follows.
function tensionRank(reading) {
  if (!reading) return 3;
  if (reading.state === 'contested') return 0;
  if (reading.state === 'no_signal') return 2;
  return 1;
}

function comparatorFor(sort, ctx) {
  const { scores, engagement, blendOf, fromId } = ctx;
  const far = (e) => farEndId(e, fromId);

  switch (sort) {
    case 'new':
      return byRecency;

    case 'hot':
      return (a, b) => {
        // attention (edges + votes across every relation type, the 2026-07-30
        // correction) — the lobby's talk formula stays the lobby's
        const ha = engagement?.[far(a)]?.attention ?? 0;
        const hb = engagement?.[far(b)]?.attention ?? 0;
        if (hb !== ha) return hb - ha;
        return byRecency(a, b);
      };

    case 'most_adjudicated':
      return (a, b) => {
        const va = (a.up_votes || 0) + (a.down_votes || 0);
        const vb = (b.up_votes || 0) + (b.down_votes || 0);
        if (vb !== va) return vb - va;
        return byRecency(a, b);
      };

    case 'strongest':
      return (a, b) => {
        const sa = scores?.[far(a)];
        const sb = scores?.[far(b)];
        // An unscored far end has no position on this axis, so it goes last
        // rather than being treated as 0 — a 0 would read as "perfectly
        // balanced", which is a claim the data does not make.
        if (!sa !== !sb) return sa ? -1 : 1;
        if (sa && sb) {
          const va = blendOf(sa.byLens);
          const vb = blendOf(sb.byLens);
          if (vb !== va) return vb - va;
        }
        return byRecency(a, b);
      };

    case 'needs_judgment':
      return (a, b) => {
        // Inert before activated: an unvoted edge is the one where a vote
        // actually produces signal. This is the activation-economics ranking —
        // it points attention at what the graph is missing rather than at what
        // it already has.
        const ia = isActivated(a) ? 1 : 0;
        const ib = isActivated(b) ? 1 : 0;
        if (ia !== ib) return ia - ib;
        // Among inert edges, longest-waiting first: oldest, so the queue drains
        // rather than churning on whatever was just drawn.
        const ta = Date.parse(a.created_at || '') || 0;
        const tb = Date.parse(b.created_at || '') || 0;
        if (ta !== tb) return ta - tb;
        return String(a.id).localeCompare(String(b.id));
      };

    case 'contested':
    default:
      return (a, b) => {
        const ra = scores?.[far(a)]?.tension;
        const rb = scores?.[far(b)]?.tension;
        const ka = tensionRank(ra);
        const kb = tensionRank(rb);
        if (ka !== kb) return ka - kb;
        // Widest NAMED tension, matching backend/lib/streamRank.ts. This used
        // to rank by `spread` — the widest gap between any two lenses, named or
        // not — which made the tree's `contested` a different quantity from the
        // front door's under the same word. The 0c fold removed that split at
        // page level on 2026-08-07 and this is the same correction one level
        // down. `top` is present on every contested reading (readTension sets
        // it whenever a pair fires), and the tensionRank guard above means the
        // fallbacks only ever run for readings that have no named tension.
        const ga = ra?.top?.gap ?? 0;
        const gb = rb?.top?.gap ?? 0;
        if (gb !== ga) return gb - ga;
        const sa = ra?.spread ?? 0;
        const sb = rb?.spread ?? 0;
        if (sb !== sa) return sb - sa;
        return byRecency(a, b);
      };
  }
}

// The relation chips to offer, with counts, for the level being shown.
// Derived from the edges actually present — the tree never advertises a filter
// that would empty the list.
export function relationOptions(edges, fromId) {
  const counts = new Map();
  for (const e of edges) {
    if (e.is_system) continue;
    counts.set(e.relation_name, (counts.get(e.relation_name) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([relation, count]) => ({ relation, count }))
    .sort((a, b) => b.count - a.count || a.relation.localeCompare(b.relation));
}

// ── OBJECT ARRANGEMENT — REMOVED 2026-08-07, and where it went ──────────────
//
// `arrangeObjects` lived here from 2026-07-31 and ranked the viewset surfaces
// client-side. It is now backend/lib/streamRank.ts, because the 0c fold put
// /resolve's ranking and the viewset ranking behind one pill row and they were
// NOT THE SAME RULE under the same name: this file's `contested` ordered by
// `tension.spread` (the widest gap between any two lenses) while /resolve
// ordered by `tension.top.gap` (the widest NAMED tension, which is the
// behaviour the docs describe). The server keeps /resolve's reading.
//
// It also could only ever rank what the feed had already returned, so
// "contested first" meant "contested within the first 500 by feed order".
//
// The EDGE comparators below stay client-side and that is not an inconsistency:
// the tree already holds every edge it renders, so it has the whole population
// in hand, and re-ranking on a pill press costs no request.

// filter + rank, in that order. Returns a new array; never mutates the input.
export function arrangeEdges(edges, fromId, opts = {}) {
  const {
    sort = DEFAULT_SORT,
    hiddenRelations,
    scores,
    engagement,
    blendOf = () => 0,
  } = opts;

  const hidden = hiddenRelations || new Set(DEFAULT_HIDDEN_RELATIONS);
  const visible = (edges || []).filter(
    (e) => !e.is_system && !hidden.has(e.relation_name)
  );

  // Sort a copy: `edges` is the prop ObjectDetail owns, and the cache entries
  // are shared with the summary walk.
  return visible
    .slice()
    .sort(comparatorFor(sort, { scores, engagement, blendOf, fromId }));
}
